import { enemyByKind } from "./enemy-defs.js";
/**
 * Client-side cosmetic effects — the first real effect layer (M6's particles
 * and lighting will grow it). Same discipline as interpolate.ts: PURE over
 * the authoritative observation + the tick + wall-clock `now` (the client is
 * a renderer; it may use the wall-clock for cosmetics, the sim never sees
 * it). The effect is DERIVED, never a source of truth:
 *
 * - The MISSILE exists only while the obs carries an in-flight attack with
 *   `kind: "projectile"` (the sim's authoritative swing window). It launches
 *   `FLIGHT_TICKS` before `impactTick` and travels a straight line in tile
 *   space from the attacker's render position to the target's — the
 *   ENDPOINT is refreshed every frame (cosmetic homing). That never
 *   contradicts authority: the sim re-checks the target's CURRENT position
 *   at impact (sim/combat.ts resolveHit), so a hit reads "caught you" and a
 *   dodge reads "missed" — the outcome is the event, the flight is scenery.
 * - At the impact tick, the combat event decides the finish: `combat-hit` ⇒
 *   an impact burst, `combat-miss` ⇒ a fizzle at the endpoint.
 *
 * Nothing here decides anything the sim cares about (AGENTS §6). No sprites,
 * no image models — every pixel is canvas primitives (the missile is
 * runtime-drawn, exactly like the click markers).
 */
import { ITEMS, TICK_MS } from "@pd/shared";
import type { AbilityFx, BlinkFx, Observation } from "@pd/shared";
import { HALF_H, HALF_W } from "./iso.js";
import { enemyKey, playerKey } from "./interpolate.js";
import type { Vec2 } from "./actions.js";

/** The missile's flight time in ticks (~200 ms at TICK_MS 50 — fixed,
 *  regardless of range: the RSPS convention. If the cast anim's release
 *  feels off vs. the launch, this constant is the tuning knob.) */
export const FLIGHT_TICKS = 4;

/** The impact burst / fizzle lifetime (ms) — pure cosmetics. */
const BURST_MS = 340;
const TRAIL_MS = 160;
/** The hitsplat lifetime (ms): hold, then fade out. */
const SPLAT_MS = 800;

/**
 * THE BASIC BOLT — the spell effect family the shade has always cast, now a
 * named, reshadeable, resizable effect (shared SPELL_TYPES "basic-bolt").
 * The CANON palette is the shade's committed look: every projectile without
 * a spell (enemies, non-magic weapons) draws it. A magic weapon's spell
 * fields (equipment.store) pick a hue + size instead. Three stops: core
 * (hot centre) / mid (body) / outer (falloff).
 */
interface BoltPalette {
  core: string;
  mid: string;
  outer: string;
}
/** The canon bolt (the shade's look — exact, unchanged). */
const CANON_BOLT: BoltPalette = { core: "#eafaff", mid: "#9fd9ff", outer: "#8c82ff" };
/** The canon bolt's head-glow radius, px at 1x (a spell's `spellRadius`
 *  replaces it; the core + trail scale off it proportionally). */
const BOLT_RADIUS = 11;
/** A reshade: the hot core stays white, body + falloff take the hue. */
function boltPalette(color: string | undefined): BoltPalette {
  return color ? { core: CANON_BOLT.core, mid: color, outer: color } : CANON_BOLT;
}
/** hex (#rrggbb) + alpha → an rgba() string (the bolt's gradient stops). */
function rgba(hex: string, alpha: number): string {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}
/** The hero's current bolt (their magic weapon's spell, or the canon).
 *  Pure view over the observation + the shared catalog — no wire field. */
function heroBolt(obs: Observation, playerId: number): { palette: BoltPalette; radius: number } {
  const p = obs.players.find((pl) => pl.id === playerId);
  const kind = p?.equipment.weapon ?? null;
  const it = kind === null ? undefined : ITEMS.find((i) => i.kind === kind);
  if (it?.weaponClass === "magic") {
    return { palette: boltPalette(it.spellColor), radius: it.spellRadius ?? BOLT_RADIUS };
  }
  return { palette: CANON_BOLT, radius: BOLT_RADIUS };
}
/** An enemy's current bolt — its npc.store spell look, or the canon. Same
 *  shape as heroBolt, one catalog lookup instead of the equipment read: the
 *  wire carries the enemy's `kind`, and the catalog is the client's copy of
 *  the store (no new wire field, no server knowledge). A wizard and an ice
 *  mephit therefore throw different-coloured bolts while the sim — which
 *  never sees a colour — stays identical for both. */
function enemyBolt(kind: string | undefined): { palette: BoltPalette; radius: number } {
  const npc = kind === undefined ? undefined : enemyByKind(kind);
  if (npc?.spellColor !== undefined || npc?.spellRadius !== undefined) {
    return {
      palette: boltPalette(npc?.spellColor),
      radius: npc?.spellRadius ?? BOLT_RADIUS,
    };
  }
  return { palette: CANON_BOLT, radius: BOLT_RADIUS };
}

/**
 * The combat event shape the effect layer consumes. The server's SimEvent is
 * sim-internal (packages/server); the wire carries plain JSON, so the client
 * narrows the fields it uses — the client never imports server types.
 */
export type CombatWireEvent = {
  type: "combat-hit" | "combat-miss" | "combat-block";
  tick: number;
  attackerIsEnemy: boolean;
  attackerId: number;
  targetIsEnemy: boolean;
  targetId: number;
  damage?: number;
  reason?: "unreachable" | "accuracy";
  /** The style stamp of the hit (the splat palette — shared/observation.ts
   *  AbilityFx): a charge kind (gold `enhanced`, pale-steel `flourish`,
   *  light-blue `chain`) or an instant ability (`black-hole` — black,
   *  `cull` — gold), reflection or a ground-fire pulse. Absent on a normal swing. */
  fx?: AbilityFx;
};

function isCombatEvent(raw: unknown): raw is CombatWireEvent {
  if (!raw || typeof raw !== "object") return false;
  const e = raw as Record<string, unknown>;
  return (
    (e.type === "combat-hit" || e.type === "combat-miss" || e.type === "combat-block") &&
    typeof e.attackerIsEnemy === "boolean" &&
    typeof e.attackerId === "number" &&
    typeof e.targetIsEnemy === "boolean" &&
    typeof e.targetId === "number"
  );
}

/** One in-flight missile, keyed by the attacker's entity key (p0/e0). */
interface Missile {
  /** The attacker's render position (tile coords) at launch. */
  from: Vec2;
  /** Wall-clock ms the flight window was first seen (pre-aged on a
   *  mid-flight join so the orb is partway along, not restarting). */
  launchAt: number;
  /** Recent orb positions (tile coords) for the afterimage trail. */
  trail: { x: number; y: number; at: number }[];
  /** The bolt's palette + size (the attacker's spell, or the canon). */
  palette: BoltPalette;
  radius: number;
}

/** A finished flight: the impact burst (hit) or the fizzle (miss). */
export interface Burst {
  x: number;
  y: number;
  at: number;
  kind: "hit" | "fizzle";
  /** The bolt's palette (hit bursts tint with it; the fizzle stays gray). */
  palette?: BoltPalette;
}

/** A damage hitsplat riding its target. Connected swings get one — melee and
 * projectile alike; chat is for the log, the splat is the at-a-glance
 * feedback. The look is keyed on the target side so dealt vs taken read
 * apart at a glance: enemy targets get the classic RSPS diamond (red +
 * damage on a hit, blue + 0 on a miss), player targets a larger jagged
 * star in a heavier palette. The position refreshes each frame while the
 * target lives/moves (cosmetic homing, same contract as the missiles). */
export interface Splat {
  x: number;
  y: number;
  at: number;
  kind: "hit" | "miss";
  damage: number;
  targetIsEnemy: boolean;
  targetId: number;
  /** The style stamp that produced the hit (the splat palette — see
   *  CombatWireEvent.fx). */
  fx?: AbilityFx;
  /** A hit negated by the target's immunity window (the gray "BLOCK" splat —
   *  the damage is the WOULD-BE number). */
  blocked?: boolean;
}

/** A Black Hole's well on the floor (the `vortex` event): the dark swirl the
 *  enemies were pulled into. Tile-anchored, ~VORTEX_MS of life. */
export interface Vortex {
  x: number;
  y: number;
  at: number;
}

/** A Blink (the `blink` event): the hero jumped from (fromX, fromY) to
 *  (toX, toY). The effect layer only RETAINS the event — the hero-shaped
 *  departure ghost and the arrival fade-in are drawn by the renderer (this
 *  pass has no hero art), which reads the list for the whole lifetime. */
export interface Blink {
  playerId: number;
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
  at: number;
  /** Which jump this was: `phoenix` (the Knight's return-to-the-death-tile,
   *  which draws the arrival flame over the ordinary fade) — absent on the
   *  Squire's plain Blink, exactly as on the wire. */
  fx?: BlinkFx;
}

/** A Telegrab (the `telegrab` event): a red spark flicks from the hero to
 *  the item, then the item's ember snaps back to the hero's hand. Pure
 *  scenery — the sim already moved the item at the cast tick (the pickup
 *  rides its own key-found / gatestone-picked event); this is the reach's
 *  read. `heroX/heroY` is the hero's position WHEN THE EVENT ARRIVED (the
 *  spark is short; no homing). */
export interface Grab {
  playerId: number;
  heroX: number;
  heroY: number;
  x: number;
  y: number;
  itemKind: "key" | "stone";
  at: number;
}

/** A Blink's retained life (ms): the departure ghost fades and the hero fades
 *  in within it. */
export const BLINK_MS = 300;
/** The arrival fade-in window (ms) inside BLINK_MS. */
export const BLINK_FADE_MS = 150;
/** A Telegrab flight (ms): the spark out over the first 45%, the item back
 *  over the rest. */
export const GRAB_MS = 460;

/** The per-frame derived picture the renderer draws (tile coords). */
export interface EffectFrame {
  missiles: { x: number; y: number; trail: { x: number; y: number; age: number }[]; palette: BoltPalette; radius: number }[];
  bursts: Burst[];
  splats: Splat[];
  vortices: Vortex[];
  /** Recent blinks (retained for BLINK_MS) — the renderer's ghost/fade read. */
  blinks: Blink[];
  /** In-flight telegrab sparks (retained for GRAB_MS). */
  grabs: Grab[];
}

/** Mutable effect state, owned by the caller (one per viewport). */
export interface EffectState {
  missiles: Map<string, Missile>;
  bursts: Burst[];
  splats: Splat[];
  vortices: Vortex[];
  blinks: Blink[];
  grabs: Grab[];
  /** The tick whose events were last consumed (one consume per tick). */
  eventsTick: number;
}

export function createEffectState(): EffectState {
  return { missiles: new Map(), bursts: [], splats: [], vortices: [], blinks: [], grabs: [], eventsTick: -1 };
}

/** A well's life on the floor (ms) — long enough to read the pull, short
 *  enough not to litter the tile. */
export const VORTEX_MS = 700;

function entityPos(obs: Observation, isEnemy: boolean, id: number): Vec2 | null {
  const e = isEnemy ? obs.enemies.find((e) => e.id === id) : obs.players.find((p) => p.id === id);
  return e ? { x: e.x, y: e.y } : null;
}

/** The interpolation key for a combatant (the same keys the renderer's
 * positions map uses — a splat rides its target's RENDER position). */
function splatKey(isEnemy: boolean, id: number): string {
  return isEnemy ? enemyKey(id) : playerKey(id);
}

/**
 * Advance the effect layer by one render frame. Mutates `st`; returns the
 * frame to draw. `events` are this obs tick's wire events (consumed once —
 * `st.eventsTick` guards re-processing between rAF frames).
 */
export function stepEffects(
  st: EffectState,
  obs: Observation,
  tick: number,
  events: unknown[] | undefined,
  positions: Map<string, Vec2>,
  now: number,
): EffectFrame {
  // 1) Missiles — one per in-flight projectile attack.
  const live = new Set<string>();
  const sources = [
    ...obs.players.map((p) => ({ key: playerKey(p.id), id: p.id, isEnemy: false, kind: undefined as string | undefined, attack: p.attack, pos: positions.get(playerKey(p.id)) ?? { x: p.x, y: p.y } })),
    ...obs.enemies.map((e) => ({ key: enemyKey(e.id), id: e.id, isEnemy: true, kind: e.kind, attack: e.attack, pos: positions.get(enemyKey(e.id)) ?? { x: e.x, y: e.y } })),
  ];
  for (const s of sources) {
    const a = s.attack;
    if (!a || a.kind !== "projectile") continue;
    const launchTick = a.impactTick - FLIGHT_TICKS;
    if (tick < launchTick || tick > a.impactTick) continue; // outside the flight window
    live.add(s.key);
    const m = st.missiles.get(s.key);
    if (!m) {
      // Pre-age a mid-flight join: the window may already be partway open.
      const bolt = s.isEnemy ? enemyBolt(s.kind) : heroBolt(obs, s.id);
      st.missiles.set(s.key, {
        from: { x: s.pos.x, y: s.pos.y },
        launchAt: now - Math.min(FLIGHT_TICKS, tick - launchTick) * TICK_MS,
        trail: [],
        palette: bolt.palette,
        radius: bolt.radius,
      });
    }
  }
  for (const k of [...st.missiles.keys()]) {
    if (!live.has(k)) st.missiles.delete(k); // resolved (impact tick) or the swing ended
  }

  // 2) The finish — this tick's combat events. EVERY swing lands a
  //    hitsplat on its target (hit ⇒ red + damage, miss ⇒ blue 0 — melee
  //    and projectile alike); the burst/fizzle finish is projectile-only
  //    (a melee swing's anim already sold it).
  if (events && st.eventsTick !== tick) {
    st.eventsTick = tick;
    for (const raw of events) {
      if (raw && typeof raw === "object" && (raw as { type?: string }).type === "enemy-blink") {
        const blink = raw as { tick: number; fromX: number; fromY: number; toX: number; toY: number };
        if (blink.tick === tick) {
          for (const tile of [{ x: blink.fromX, y: blink.fromY }, { x: blink.toX, y: blink.toY }]) {
            st.bursts.push({ ...tile, at: now, kind: "hit", palette: boltPalette("#76dcff") });
          }
        }
        continue;
      }
      // A Black Hole's well (the `vortex` event): mark the tile — the pull
      // itself rides the enemy positions (the interp's stretched lerp).
      if (
        raw && typeof raw === "object" && (raw as { type?: unknown }).type === "vortex" &&
        (raw as { tick?: unknown }).tick === tick &&
        typeof (raw as { x?: unknown }).x === "number" &&
        typeof (raw as { y?: unknown }).y === "number"
      ) {
        const v = raw as { x: number; y: number };
        st.vortices.push({ x: v.x, y: v.y, at: now });
        continue;
      }
      // A Blink (the `blink` event): retain it for the renderer's hero ghost
      // + fade-in. Nothing is drawn in this pass (no hero art here).
      if (
        raw && typeof raw === "object" && (raw as { type?: unknown }).type === "blink" &&
        (raw as { tick?: unknown }).tick === tick
      ) {
        const b = raw as { playerId?: unknown; fromX?: unknown; fromY?: unknown; x?: unknown; y?: unknown; fx?: unknown };
        if (typeof b.playerId === "number" && typeof b.fromX === "number" && typeof b.fromY === "number" && typeof b.x === "number" && typeof b.y === "number") {
          st.blinks.push({
            playerId: b.playerId,
            fromX: b.fromX,
            fromY: b.fromY,
            toX: b.x,
            toY: b.y,
            at: now,
            ...(b.fx === "phoenix" ? { fx: "phoenix" as const } : {}),
          });
        }
        continue;
      }
      // A Telegrab (the `telegrab` event): the spark's endpoints. The hero's
      // position here is its CURRENT render position (the grab starts from
      // wherever the hero is when the event lands).
      if (
        raw && typeof raw === "object" && (raw as { type?: unknown }).type === "telegrab" &&
        (raw as { tick?: unknown }).tick === tick
      ) {
        const g = raw as { playerId?: unknown; itemKind?: unknown; x?: unknown; y?: unknown };
        const heroPos = typeof g.playerId === "number" ? positions.get(playerKey(g.playerId)) : undefined;
        if (heroPos && typeof g.playerId === "number" && typeof g.x === "number" && typeof g.y === "number") {
          st.grabs.push({
            playerId: g.playerId,
            heroX: heroPos.x,
            heroY: heroPos.y,
            x: g.x,
            y: g.y,
            itemKind: g.itemKind === "stone" ? "stone" : "key",
            at: now,
          });
        }
        continue;
      }
      if (!isCombatEvent(raw) || raw.tick !== tick) continue;
      const tp = positions.get(splatKey(raw.targetIsEnemy, raw.targetId))
        ?? entityPos(obs, raw.targetIsEnemy, raw.targetId);
      if (!tp) continue;
      const blocked = raw.type === "combat-block";
      const hit = raw.type === "combat-hit";
      if (raw.type !== "combat-miss" || raw.reason !== "unreachable") st.splats.push({
        x: tp.x,
        y: tp.y,
        at: now,
        // A blocked hit is a HIT that did nothing (gray "BLOCK"); a miss is
        // the fizzle. Everything else that isn't a miss is a landed hit.
        kind: hit || blocked ? "hit" : "miss",
        damage: hit || blocked ? (raw.damage ?? 0) : 0,
        targetIsEnemy: raw.targetIsEnemy,
        targetId: raw.targetId,
        fx: hit ? (raw.fx ?? undefined) : undefined,
        blocked: blocked || undefined,
      });
      const attackerEnemy = raw.attackerIsEnemy
        ? obs.enemies.find((e) => e.id === raw.attackerId)
        : undefined;
      const attacker = attackerEnemy ?? obs.players.find((p) => p.id === raw.attackerId);
      if (!attacker || attacker.attack?.kind !== "projectile") continue;
      if (attacker.attack.impactTick !== tick) continue; // not THIS swing's resolution
      // The impact burst is projectile-only (a melee swing's anim already
      // sold it); a blocked hit has no burst — the damage was negated.
      if (blocked) continue;
      st.bursts.push({
        x: tp.x,
        y: tp.y,
        at: now,
        kind: hit ? "hit" : "fizzle",
        palette: attackerEnemy ? enemyBolt(attackerEnemy.kind).palette
                               : heroBolt(obs, raw.attackerId).palette,
      });
    }
  }

  // 3) Current orb positions (homed endpoint) + trail upkeep.
  const missiles: EffectFrame["missiles"] = [];
  for (const s of sources) {
    const a = s.attack;
    if (!a || a.kind !== "projectile") continue;
    const m = st.missiles.get(s.key);
    if (!m) continue;
    const tp = entityPos(obs, a.targetIsEnemy, a.targetId);
    if (!tp) continue;
    const t = Math.min(1, (now - m.launchAt) / (FLIGHT_TICKS * TICK_MS));
    const x = m.from.x + (tp.x - m.from.x) * t;
    const y = m.from.y + (tp.y - m.from.y) * t;
    m.trail.push({ x, y, at: now });
    while (m.trail.length > 0 && now - m.trail[0]!.at > TRAIL_MS) m.trail.shift();
    missiles.push({
      x,
      y,
      trail: m.trail.map((p) => ({ x: p.x, y: p.y, age: (now - p.at) / TRAIL_MS })),
      palette: m.palette,
      radius: m.radius,
    });
  }
  st.bursts = st.bursts.filter((b) => now - b.at <= BURST_MS);
  // 4) Hitsplat upkeep — home to the target while it is still on the floor
  //    (a kiting hero carries its splats), expire after SPLAT_MS. The
  //    INTERPOLATED position is preferred (the sprite renders there — an
  //    obs-tile position can be half a step behind mid-stride).
  for (const s of st.splats) {
    const tp = positions.get(splatKey(s.targetIsEnemy, s.targetId)) ?? entityPos(obs, s.targetIsEnemy, s.targetId);
    if (tp) {
      s.x = tp.x;
      s.y = tp.y;
    }
  }
  st.splats = st.splats.filter((s) => now - s.at <= SPLAT_MS);
  st.vortices = st.vortices.filter((v) => now - v.at <= VORTEX_MS);
  st.blinks = st.blinks.filter((b) => now - b.at <= BLINK_MS);
  st.grabs = st.grabs.filter((g) => now - g.at <= GRAB_MS);
  return { missiles, bursts: st.bursts, splats: st.splats, vortices: st.vortices, blinks: st.blinks, grabs: st.grabs };
}

// ---- drawing (canvas primitives only — the missile is runtime-drawn) -----

/**
 * Draw the effect frame in world space. The caller must have already applied
 * the camera transform (the viewport re-enters the renderer's translated
 * space); tile coords project with the same 2:1 dimetric math as render.ts.
 * `u` is the iso unit (css px per half-tile-step).
 */
export function drawEffects(
  ctx: CanvasRenderingContext2D,
  fx: EffectFrame,
  u: number,
  now: number,
): void {
  // The same 2:1 dimetric projection as render.ts (tile anchor = the
  // diamond's TOP corner; the tile centre is HALF_H below it). The orb
  // hovers just above the ground plane (cy − 14u).
  const project = (t: Vec2): [number, number, number] => {
    const sx = (t.x - t.y) * HALF_W * u;
    const sy = (t.x + t.y) * HALF_H * u;
    return [sx, sy + HALF_H * u - 14 * u, 1];
  };

  // Black Holes — GROUND effects, drawn first (under everything): a violet
  // flash, a shrinking black core (the well) and a ring that contracts as
  // the well opens, then expands as it closes. The drag itself is the
  // interpolation layer's stretched lerp on the pulled enemies.
  for (const v of fx.vortices) {
    const age = (now - v.at) / VORTEX_MS;
    if (age >= 1) continue;
    const [sx, hoverY] = project({ x: v.x, y: v.y });
    const gy = hoverY + 14 * u; // back onto the ground plane (project hovers)
    ctx.save();
    const fr = (10 + 16 * age) * u;
    const g = ctx.createRadialGradient(sx, gy, 0, sx, gy, fr);
    g.addColorStop(0, `rgba(120,90,220,${(1 - age) * 0.5})`);
    g.addColorStop(1, "rgba(120,90,220,0)");
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.ellipse(sx, gy, fr, fr * 0.42, 0, 0, Math.PI * 2);
    ctx.fill();
    const cr = Math.max(0, (9 - 6 * age) * u);
    if (cr > 0.5 * u) {
      ctx.globalAlpha = (1 - age * 0.8) * 0.95;
      ctx.fillStyle = "#0a0a10";
      ctx.beginPath();
      ctx.ellipse(sx, gy, cr, cr * 0.42, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1;
    }
    const ringR = (age < 0.25 ? 28 - 40 * age : 8 + 32 * (age - 0.25)) * u;
    ctx.globalAlpha = (1 - age) * 0.8;
    ctx.strokeStyle = "rgba(190,160,255,0.9)";
    ctx.lineWidth = Math.max(1, 2 * u * (1 - age));
    ctx.beginPath();
    ctx.ellipse(sx, gy, ringR, ringR * 0.42, 0, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }

  for (const b of fx.bursts) {
    const age = (now - b.at) / BURST_MS;
    if (age >= 1) continue;
    const [sx, cy] = project(b);
    if (b.kind === "hit") {
      // Expanding ring + a decaying flash, in the bolt's palette (the
      // canon hue for enemies / non-magic weapons).
      const pal = b.palette ?? CANON_BOLT;
      const r = (6 + 26 * age) * u;
      ctx.save();
      ctx.globalAlpha = (1 - age) * 0.9;
      ctx.strokeStyle = pal.mid;
      ctx.lineWidth = Math.max(1, 2.5 * u * (1 - age));
      ctx.beginPath();
      ctx.arc(sx, cy, r, 0, Math.PI * 2);
      ctx.stroke();
      const g = ctx.createRadialGradient(sx, cy, 0, sx, cy, r * 0.8);
      g.addColorStop(0, rgba(pal.core, (1 - age) * 0.9));
      g.addColorStop(0.4, rgba(pal.mid, (1 - age) * 0.5));
      g.addColorStop(1, rgba(pal.mid, 0));
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(sx, cy, r * 0.8, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    } else {
      // Fizzle: a small gray-violet puff that fades (the shot missed).
      ctx.save();
      ctx.globalAlpha = (1 - age) * 0.7;
      const r = (4 + 10 * age) * u;
      const g = ctx.createRadialGradient(sx, cy, 0, sx, cy, r);
      g.addColorStop(0, `rgba(150, 140, 190, ${(1 - age) * 0.8})`);
      g.addColorStop(1, "rgba(150, 140, 190, 0)");
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(sx, cy - age * 8 * u, r, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
  }

  for (const m of fx.missiles) {
    // Afterimage trail first (faded orbs along the recent path), then the
    // head (a bright core in a soft glow — the bolt's palette, canon by
    // default). Sizes scale off `radius` (the canon bolt's 11 → the exact
    // historic pixel values).
    const pal = m.palette;
    for (const p of m.trail) {
      if (p.age >= 1) continue;
      const [sx, cy] = project(p);
      ctx.save();
      ctx.globalAlpha = 1 - p.age;
      ctx.fillStyle = rgba(pal.mid, 0.35);
      ctx.beginPath();
      ctx.arc(sx, cy, (m.radius * 3.2) / 11 * u * (1 - p.age * 0.6), 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
    const [sx, cy] = project(m);
    ctx.save();
    const glow = ctx.createRadialGradient(sx, cy, 0, sx, cy, m.radius * u);
    glow.addColorStop(0, rgba(pal.core, 0.95));
    glow.addColorStop(0.3, rgba(pal.mid, 0.75));
    glow.addColorStop(0.65, rgba(pal.outer, 0.35));
    glow.addColorStop(1, rgba(pal.outer, 0));
    ctx.fillStyle = glow;
    ctx.beginPath();
    ctx.arc(sx, cy, m.radius * u, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#f4fcff";
    ctx.beginPath();
    ctx.arc(sx, cy, (m.radius * 3.4) / 11 * u, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  // Telegrab — an AIR effect (over the ground wells/bursts, under the
  // splats): a red spark flicks from the Squire's hand to the item, then the
  // item's ember snaps back into the hand. The pickup already happened (the
  // sim resolved it at the cast tick) — this is the reach's read.
  for (const g of fx.grabs) {
    const t = (now - g.at) / GRAB_MS;
    if (t >= 1) continue;
    const [hx, hyRaw] = project({ x: g.heroX, y: g.heroY });
    const [ix, iyRaw] = project({ x: g.x, y: g.y });
    const hy = hyRaw + 14 * u - 24 * u; // the hand: mid-body (project hovers 14u above ground)
    const iy = iyRaw + 14 * u - 6 * u; // just above the item on the ground
    const outbound = t < 0.45;
    const p = outbound ? t / 0.45 : (t - 0.45) / 0.55;
    // The travelling point: hand -> item on the way out (a lean red spark),
    // item -> hand on the way back (the pulled item, brighter + bigger).
    const px = outbound ? hx + (ix - hx) * p : ix + (hx - ix) * p;
    const py = outbound ? hy + (iy - hy) * p : iy + (hy - iy) * p;
    const fade = t < 0.8 ? 1 : 1 - (t - 0.8) / 0.2;
    const r = (outbound ? 3.2 : 5.2) * u;
    ctx.save();
    ctx.globalAlpha = fade;
    // A short comet trail behind the travelling point.
    const tail = outbound ? hx + (ix - hx) * Math.max(0, p - 0.35) : ix + (hx - ix) * Math.max(0, p - 0.35);
    const tailY = outbound ? hy + (iy - hy) * Math.max(0, p - 0.35) : iy + (hy - iy) * Math.max(0, p - 0.35);
    const grd = ctx.createLinearGradient(tail, tailY, px, py);
    grd.addColorStop(0, "rgba(255,74,61,0)");
    grd.addColorStop(1, "rgba(255,96,72,0.6)");
    ctx.strokeStyle = grd;
    ctx.lineWidth = Math.max(1, 1.8 * u);
    ctx.beginPath();
    ctx.moveTo(tail, tailY);
    ctx.lineTo(px, py);
    ctx.stroke();
    // The spark itself: a hot core inside a red glow.
    const glow = ctx.createRadialGradient(px, py, 0, px, py, r * 2.6);
    glow.addColorStop(0, "rgba(255,140,110,0.85)");
    glow.addColorStop(1, "rgba(255,74,61,0)");
    ctx.fillStyle = glow;
    ctx.beginPath();
    ctx.arc(px, py, r * 2.6, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = outbound ? "#ff7a5c" : "#ffe0d2";
    ctx.beginPath();
    ctx.arc(px, py, r, 0, Math.PI * 2);
    ctx.fill();
    // The landing flash on the item (the moment the spark touches it).
    if (outbound) {
      const land = Math.max(0, (p - 0.72) / 0.28);
      if (land > 0) {
        ctx.globalAlpha = fade * land;
        ctx.strokeStyle = "rgba(255,150,120,0.9)";
        ctx.lineWidth = Math.max(1, 1.5 * u);
        ctx.beginPath();
        ctx.arc(ix, iy, (5 + 9 * land) * u, 0, Math.PI * 2);
        ctx.stroke();
      }
    }
    ctx.restore();
  }

  // Hitsplats — drawn LAST (they must read over the combatant, its HP bar
  // area and any burst). TWO looks, keyed on the target side so "damage
  // I'm dealing" and "damage I'm taking" are told apart at a glance:
  //   - dealt (target an enemy): a DIAMOND — red + white damage on a hit,
  //     steel blue + 0 on a miss (the RSPS convention);
  //   - taken (target a player): a larger, jagged STAR — deep crimson +
  //     damage on a hit, slate + 0 on a miss ("this one is about ME").
  // Both hold ~70% of their life, then fade while drifting up (canvas
  // primitives only).
  for (const s of fx.splats) {
    const age = (now - s.at) / SPLAT_MS;
    if (age >= 1) continue;
    const [sx, cy] = project({ x: s.x, y: s.y });
    // Mid-body of a 96-px billboard (ground centre − ~40u) + the rise.
    const y = cy - 26 * u - age * 14 * u;
    const alpha = age < 0.7 ? 1 : 1 - (age - 0.7) / 0.3;
    const hit = s.kind === "hit";
    const blocked = s.blocked === true;
    const charge = s.fx === "ground-fire" || s.fx === "meteor" ? undefined : s.fx;
    // Charge hits read BIGGER + brighter (the ability's "punch"); they are
    // always dealt (the hero's offensive abilities), so they are diamonds.
    const isStar = !s.targetIsEnemy && !charge;
    const big = charge !== undefined;
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.translate(sx, y);
    // A soft glow behind a charge splat (the "punch in" — it lands hot).
    // Gold (enhanced, cull), pale steel (flourish, chain), violet (the
    // black hole's well — a dark glow, so the black badge reads on any tile).
    if (charge) {
      const gr = (big ? 22 : 16) * u;
      const g = ctx.createRadialGradient(0, 0, 0, 0, 0, gr);
      g.addColorStop(
        0,
        charge === "enhanced" || charge === "cull"
          ? "rgba(255,200,60,0.55)"
          : charge === "black-hole" || charge === "reflect"
            ? "rgba(130,95,230,0.6)"
            : "rgba(170,220,255,0.5)",
      );
      g.addColorStop(1, "rgba(255,255,255,0)");
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(0, 0, gr, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.beginPath();
    if (isStar) {
      // Taken: the jagged star — an 8-point burst, ~15% larger than the
      // diamond, in a heavier palette (deep crimson on a hit, slate on a
      // miss; stone gray when blocked) with a hot rim so it pops.
      const ro = 13.5 * u;
      const ri = ro * 0.55;
      for (let i = 0; i < 8; i++) {
        const ao = (i * Math.PI) / 4;
        const ai = ao + Math.PI / 8;
        if (i === 0) ctx.moveTo(Math.cos(ao) * ro, Math.sin(ao) * ro);
        else ctx.lineTo(Math.cos(ao) * ro, Math.sin(ao) * ro);
        ctx.lineTo(Math.cos(ai) * ri, Math.sin(ai) * ri);
      }
      ctx.closePath();
      ctx.fillStyle = blocked ? "#3a3f47" : hit ? "#6e100b" : "#39414f";
      ctx.strokeStyle = blocked ? "#9aa2ad" : hit ? "#e0866f" : "#8d97a8";
      ctx.lineWidth = Math.max(1, 1.8 * u);
    } else {
      // Dealt: the diamond (a rotated rounded square). Charge hits are ~25%
      // larger and hot (gold / pale steel); blocked reads stone gray.
      ctx.rotate(Math.PI / 4);
      const r = (big ? 12.5 : 10) * u;
      // The badge palette (the user's call): gold for the empowered (enhanced
      // + cull), light blue for the arcane flow (flourish + chain — the
      // chain reads like its parent's splash), BLACK for the well (black-hole
      // — the white rim keeps it readable on any tile).
      ctx.fillStyle = blocked
        ? "#565c66"
        : charge === "enhanced" || charge === "cull"
          ? "#f5b301"
          : charge === "flourish" || charge === "chain"
            ? "#bfe4ff"
            : charge === "black-hole"
              ? "#14141a"
              : charge === "reflect"
                ? "#a978ef"
              : hit
                ? "#c22a24"
                : "#2c5aa0";
      ctx.strokeStyle = blocked ? "#9aa2ad" : charge ? "#ffffff" : "rgba(0,0,0,0.8)";
      ctx.lineWidth = Math.max(1, 1.5 * u);
      if (typeof ctx.roundRect === "function") ctx.roundRect(-r, -r, 2 * r, 2 * r, 2.5 * u);
      else ctx.rect(-r, -r, 2 * r, 2 * r);
    }
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    // The label upright (outside the badge's transform): the damage number on
    // a hit, "BLOCK" (with the would-be number) on a negated hit, one size up
    // for the taken star so it reads first.
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    if (blocked) {
      ctx.fillStyle = "#d7d2c4";
      ctx.font = `bold ${Math.max(9, 10 * u)}px ui-monospace, Menlo, monospace`;
      ctx.fillText(`BLOCK ${s.damage}`, sx, y + 0.5 * u);
    } else {
      ctx.fillStyle = charge ? "#ffffff" : "#ffffff";
      ctx.font = `bold ${Math.max(10, (isStar ? 12.5 : big ? 13 : 11) * u)}px ui-monospace, Menlo, monospace`;
      ctx.fillText(String(s.damage), sx, y + 0.5 * u);
    }
    ctx.restore();
  }
}
